/**
 * Shared utilities for resolving maturity-level colors from the DB-loaded levels.
 */
import { useEngineMaturityLevels } from './hooks'
import type { MaturityLevel } from './types'

const FALLBACK_COLOR = '#6b7280'  // gray-500

/**
 * Pure helper — looks up the hex color for a given level number.
 * Returns a neutral gray when the level isn't found.
 */
export function getMaturityColorForLevel(
    levels: MaturityLevel[] | undefined,
    level: number | null | undefined,
): string {
    if (!levels || level == null) return FALLBACK_COLOR
    return levels.find(l => l.level === level)?.color || FALLBACK_COLOR
}

/**
 * Pure helper — looks up the name for a given level number.
 */
export function getMaturityNameForLevel(
    levels: MaturityLevel[] | undefined,
    level: number | null | undefined,
): string {
    if (!levels || level == null) return ''
    return levels.find(l => l.level === level)?.name ?? `Level ${level}`
}

/**
 * React hook — returns a lookup map (level → { color, name }) built from the
 * engine maturity-levels query.  Callers can use it for O(1) lookups.
 */
export function useMaturityColorMap() {
    const { data: raw } = useEngineMaturityLevels()
    const levels: MaturityLevel[] = (raw as any)?.data ?? raw ?? []

    const map = new Map<number, { color: string; name: string }>()
    for (const l of levels) {
        map.set(l.level, { color: l.color || FALLBACK_COLOR, name: l.name })
    }

    return { map, levels }
}
